import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiAdmin from '../services/api_admin'; // Importa a nova instância apiAdmin
import { useAdminAuth } from '../context/AdminAuthContext';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import { AnimatePresence } from 'framer-motion';
import AdminLayout from '../components/AdminLayout';
import AdminFilterBar, { AdminSearchField } from '../components/ui/AdminFilterBar';
import AdminPagination from '../components/ui/AdminPagination';
import { AdminLoadingState, AdminEmptyState } from '../components/ui/AdminStates';
import Alert from '../components/ui/Alert';
import './AdminDashboard.css';
import { AxiosError } from 'axios';

/* ================== Tipos ================== */
interface User {
  id_user: number;
  cpf: string;
  name: string | null; // nome pode vir null
  email: string;
  special_services: number;
  user_level: string;
}

interface Protocol {
  id_protocol: number;
  protocol: string;
  date: string;
  time: string;
  situation: string;
  id_user: number;
}

interface ApiErrorResponse {
  message?: string;
  error?: string;
}

/* ================== Constantes ================== */
// Cores derivadas dos tokens de marca (--brand, --success, --admin-warning)
const SITUATION_COLORS: Record<string, string> = {
  'pendente': '#5b6b84',
  'Documento Pendente': '#b45309',
  'Aprovado': '#1c8c5e',
};
const BAR_COLOR = '#1955bf';
const PAGE_SIZE = 10;

const SITUATION_BADGE: Record<string, 'success' | 'warning' | 'neutral'> = {
  'Aprovado': 'success',
  'Documento Pendente': 'warning',
  'pendente': 'neutral',
};

/* ================== Helpers ================== */
// Mostra vazio quando name vier null/vazio
const getDisplayName = (name: string | null) => (name?.trim() ?? '');

// Formata data para DD/MM/YYYY
const formatBrazilianDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const AdminDashboard: React.FC = () => {
  const { adminToken } = useAdminAuth();
  const navigate = useNavigate();

  const [users, setUsers] = useState<User[]>([]);
  const [protocols, setProtocols] = useState<Protocol[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingProtocols, setLoadingProtocols] = useState(true);
  const [userPage, setUserPage] = useState(1);
  const [protocolPage, setProtocolPage] = useState(1);
  const [userFilter, setUserFilter] = useState({ cpf: '', id_user: '' });
  const [protocolFilter, setProtocolFilter] = useState({ protocol: '', situation: '', month_year: '' });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!adminToken) {
      setErrorMessage('Sessão expirada. Faça login novamente.');
      navigate('/rj/admin-login');
      return;
    }

    const fetchUsers = async () => {
      try {
        const res = await apiAdmin.get<{ users: User[] }>('/auth/users');
        setUsers(res.data.users || []);
      } catch (err) {
        const error = err as AxiosError<ApiErrorResponse>;
        console.error('Erro ao carregar usuários:', error.response?.status, error.response?.data);
        setErrorMessage(error.response?.data?.message || 'Falha ao carregar usuários.');
      } finally {
        setLoadingUsers(false);
      }
    };

    const fetchProtocols = async () => {
      try {
        const res = await apiAdmin.get<{ message: string; count: number; data: Protocol[] }>('/protocol');
        if (res.data.message === 'Protocols retrieved') {
          setProtocols(res.data.data || []);
        } else {
          throw new Error('Resposta inválida ao buscar protocolos.');
        }
      } catch (err) {
        const error = err as AxiosError<ApiErrorResponse>;
        console.error('Erro ao carregar protocolos:', error.response?.status, error.response?.data);
        setErrorMessage(error.response?.data?.message || 'Falha ao carregar protocolos.');
      } finally {
        setLoadingProtocols(false);
      }
    };

    fetchUsers();
    fetchProtocols();
  }, [adminToken, navigate]);

  /* ================== Filtragens ================== */
  const filteredUsers = users.filter(
    (u) =>
      (u.cpf ?? '').toLowerCase().includes(userFilter.cpf.toLowerCase()) &&
      (userFilter.id_user === '' || u.id_user.toString().includes(userFilter.id_user))
  );

  const filteredProtocols = protocols.filter(
    (p) =>
      (p.protocol ?? '').toLowerCase().includes(protocolFilter.protocol.toLowerCase()) &&
      (p.situation ?? '').toLowerCase().includes(protocolFilter.situation.toLowerCase()) &&
      (protocolFilter.month_year === '' || (p.date ?? '').slice(0, 7) === protocolFilter.month_year)
  );

  // último protocolo por usuário
  const latestProtocols = Object.values(
    filteredProtocols.reduce((acc: { [key: number]: Protocol }, protocol) => {
      const currentDate = new Date(protocol.date).getTime();
      if (!acc[protocol.id_user] || currentDate > new Date(acc[protocol.id_user].date).getTime()) {
        acc[protocol.id_user] = protocol;
      }
      return acc;
    }, {})
  );

  // pizza por situação
  const situationCounts = ['pendente', 'Documento Pendente', 'Aprovado'].map((status) => ({
    name: status,
    value: filteredProtocols.filter((p) => p.situation === status).length,
  }));

  // barras por data
  const protocolByDate = filteredProtocols.reduce((acc: { [key: string]: number }, p) => {
    const formattedDate = formatBrazilianDate(p.date);
    acc[formattedDate] = (acc[formattedDate] || 0) + 1;
    return acc;
  }, {});

  const barData = Object.entries(protocolByDate)
    .map(([date, count]) => ({ date, count }))
    .sort((a, b) => {
      const [dayA, monthA, yearA] = a.date.split('/').map(Number);
      const [dayB, monthB, yearB] = b.date.split('/').map(Number);
      return new Date(yearA, monthA - 1, dayA).getTime() - new Date(yearB, monthB - 1, dayB).getTime();
    });

  /* ================== Paginação ================== */
  const totalUserPages = Math.ceil(filteredUsers.length / PAGE_SIZE) || 1;
  const totalProtocolPages = Math.ceil(latestProtocols.length / PAGE_SIZE) || 1;
  const pagedUsers = filteredUsers.slice((userPage - 1) * PAGE_SIZE, userPage * PAGE_SIZE);
  const pagedProtocols = latestProtocols.slice((protocolPage - 1) * PAGE_SIZE, protocolPage * PAGE_SIZE);

  /* ================== Chips de filtro ativo ================== */
  const userChips = [
    userFilter.cpf && { key: 'cpf', label: `CPF: ${userFilter.cpf}`, onRemove: () => setUserFilter((f) => ({ ...f, cpf: '' })) },
    userFilter.id_user && { key: 'id', label: `ID: ${userFilter.id_user}`, onRemove: () => setUserFilter((f) => ({ ...f, id_user: '' })) },
  ].filter(Boolean) as { key: string; label: string; onRemove: () => void }[];

  const protocolChips = [
    protocolFilter.protocol && { key: 'protocol', label: `Protocolo: ${protocolFilter.protocol}`, onRemove: () => setProtocolFilter((f) => ({ ...f, protocol: '' })) },
    protocolFilter.situation && { key: 'situation', label: `Situação: ${protocolFilter.situation}`, onRemove: () => setProtocolFilter((f) => ({ ...f, situation: '' })) },
    protocolFilter.month_year && { key: 'month', label: `Mês: ${protocolFilter.month_year}`, onRemove: () => setProtocolFilter((f) => ({ ...f, month_year: '' })) },
  ].filter(Boolean) as { key: string; label: string; onRemove: () => void }[];

  return (
    <AdminLayout title="Painel de Administração">
      <AnimatePresence>
        {errorMessage && <Alert variant="error">{errorMessage}</Alert>}
      </AnimatePresence>

      {/* Tabela de Usuários */}
      <section className="admin-card">
        <div className="admin-card__head">
          <h2>Usuários</h2>
        </div>

        <AdminFilterBar
          chips={userChips}
          onClearAll={() => setUserFilter({ cpf: '', id_user: '' })}
          resultCount={filteredUsers.length}
          resultLabel="usuários"
        >
          <AdminSearchField
            id="user-cpf"
            label="CPF"
            value={userFilter.cpf}
            onChange={(v) => setUserFilter({ ...userFilter, cpf: v })}
            placeholder="Buscar por CPF"
          />
          <AdminSearchField
            id="user-id"
            label="ID do usuário"
            value={userFilter.id_user}
            onChange={(v) => setUserFilter({ ...userFilter, id_user: v })}
            placeholder="Buscar por ID"
          />
        </AdminFilterBar>

        {loadingUsers ? (
          <AdminLoadingState label="Carregando usuários..." />
        ) : pagedUsers.length === 0 ? (
          <AdminEmptyState message="Nenhum usuário encontrado para os filtros aplicados." />
        ) : (
          <>
            <div className="admin-table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>ID User</th>
                    <th>CPF</th>
                    <th>Nome</th>
                    <th>Serviços Especiais</th>
                    <th>Email</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedUsers.map((u) => (
                    <tr key={u.id_user}>
                      <td>{u.id_user}</td>
                      <td>{u.cpf}</td>
                      <td>{getDisplayName(u.name)}</td>
                      <td>{u.special_services ? 'Sim' : 'Não'}</td>
                      <td>{u.email}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <AdminPagination page={userPage} totalPages={totalUserPages} onChange={setUserPage} />
          </>
        )}
      </section>

      {/* Gráficos */}
      <div className="charts-container">
        <section className="admin-card chart-section">
          <div className="admin-card__head">
            <h2>Resumo de Protocolos</h2>
          </div>
          {loadingProtocols ? (
            <AdminLoadingState label="Carregando dados do gráfico..." />
          ) : (
            <div style={{ width: '100%', height: 300 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={situationCounts} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label>
                    {situationCounts.map((entry, idx) => (
                      <Cell key={`cell-${idx}`} fill={SITUATION_COLORS[entry.name] || BAR_COLOR} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend verticalAlign="bottom" />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>

        <section className="admin-card chart-section">
          <div className="admin-card__head">
            <h2>Protocolos por Data</h2>
          </div>
          {loadingProtocols ? (
            <AdminLoadingState label="Carregando dados do gráfico..." />
          ) : (
            <div style={{ width: '100%', height: 300 }}>
              <ResponsiveContainer>
                <BarChart data={barData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#d8e0ec" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="count" fill={BAR_COLOR} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>
      </div>

      {/* Tabela de Protocolos */}
      <section className="admin-card">
        <div className="admin-card__head">
          <h2>Protocolos Pendentes</h2>
        </div>

        <AdminFilterBar
          chips={protocolChips}
          onClearAll={() => setProtocolFilter({ protocol: '', situation: '', month_year: '' })}
          resultCount={latestProtocols.length}
          resultLabel="protocolos"
        >
          <AdminSearchField
            id="protocol-number"
            label="Número do protocolo"
            value={protocolFilter.protocol}
            onChange={(v) => setProtocolFilter({ ...protocolFilter, protocol: v })}
            placeholder="Buscar protocolo"
          />
          <AdminSearchField
            id="protocol-situation"
            label="Situação"
            value={protocolFilter.situation}
            onChange={(v) => setProtocolFilter({ ...protocolFilter, situation: v })}
            placeholder="Buscar situação"
          />
          <div className="admin-filter-field">
            <label htmlFor="protocol-month-year">Mês e ano</label>
            <input
              id="protocol-month-year"
              type="month"
              value={protocolFilter.month_year}
              onChange={(e) => setProtocolFilter({ ...protocolFilter, month_year: e.target.value })}
              className="admin-input"
              aria-label="Filtrar por mês e ano do protocolo"
            />
          </div>
        </AdminFilterBar>

        {loadingProtocols ? (
          <AdminLoadingState label="Carregando protocolos..." />
        ) : pagedProtocols.length === 0 ? (
          <AdminEmptyState message="Nenhum protocolo encontrado para os filtros aplicados." />
        ) : (
          <>
            <div className="admin-table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>ID Protocol</th>
                    <th>ID User</th>
                    <th>Nome do Usuário</th>
                    <th>Email do Usuário</th>
                    <th>Protocolo</th>
                    <th>Data</th>
                    <th>Hora</th>
                    <th>Situação</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedProtocols.map((p) => {
                    const user = users.find((u) => u.id_user === p.id_user);
                    return (
                      <tr key={p.id_protocol}>
                        <td>{p.id_protocol}</td>
                        <td>{p.id_user}</td>
                        <td>{user ? getDisplayName(user.name) : ''}</td>
                        <td>{user ? user.email : 'Desconhecido'}</td>
                        <td>{p.protocol}</td>
                        <td>{formatBrazilianDate(p.date)}</td>
                        <td>{p.time}</td>
                        <td>
                          <span className={`badge badge--${SITUATION_BADGE[p.situation] || 'neutral'}`}>{p.situation}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <AdminPagination page={protocolPage} totalPages={totalProtocolPages} onChange={setProtocolPage} />
          </>
        )}
      </section>
    </AdminLayout>
  );
};

export default AdminDashboard;
